// src/components/digital_twin/AssetDetailModal.js
// ═══════════════════════════════════════════════════════════════════════════
// Enhanced Asset Detail Modal — Production Ready
// ═══════════════════════════════════════════════════════════════════════════
// Features:
//   - Live WebSocket Telemetry Data Parsing
//   - Context-Aware Sensor & Pipeline Topology Mapping
//   - Digital Twin Drawer Integration
//   - Fixed light/dark theme text visibility
//   - Enhanced visual design and spacing
//   - Improved animations and transitions
//   - Better mobile responsiveness
// ═══════════════════════════════════════════════════════════════════════════
// --- UPDATE: v2.0
//   • Uses data the Digital Twin page already holds (issues, sensors, bulk health)
//     instead of downloading EVERY issue and EVERY sensor on each open; falls back
//     to fetching only when not supplied. Remaining fetches run in parallel.
//   • Stale-response guard: switching assets quickly can no longer show the
//     previous asset's data.
//   • New "Simulation" tab: per-asset hurricane / flood / cascade impact from the
//     active simulation (damage probabilities, flood timing, cost, restoration,
//     recommended pre-storm actions).
//   • Export via Blob (and includes simulation impact).
// --- END UPDATE

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton,
  ModalBody, ModalFooter, Button, Text, VStack, Divider, Tag, Flex,
  Tabs, TabList, TabPanels, Tab, TabPanel, Box, Badge, HStack,
  Progress, Stat, StatLabel, StatNumber, StatHelpText,
  Table, Thead, Tbody, Tr, Th, Td, IconButton, useToast, Spinner,
  Grid, GridItem, Tooltip, Alert, AlertIcon, AlertTitle, AlertDescription,
  useColorModeValue, Skeleton, Icon
} from '@chakra-ui/react';
import { 
  DownloadIcon, ExternalLinkIcon, RepeatIcon, WarningIcon, 
  InfoIcon, TimeIcon, SettingsIcon, CheckCircleIcon,
} from '@chakra-ui/icons';
import { FaRobot, FaWind } from 'react-icons/fa';
import { motion } from 'framer-motion';
import * as PredictiveFailureService from '../../services/predictiveFailureService';
import * as IssueService from '../../services/issueService';
import * as SensorService from '../../services/sensorService';

const MotionBox = motion(Box);
const MotionGrid = motion(Grid);

// ═════════════════════════════════════════════════════════════════════════════
// ANIMATION VARIANTS
// ═════════════════════════════════════════════════════════════════════════════
const fadeIn = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.3 } },
};

const stagger = {
  visible: { transition: { staggerChildren: 0.05 } },
};

// ═════════════════════════════════════════════════════════════════════════════
// UTILITY FUNCTIONS
// ═════════════════════════════════════════════════════════════════════════════
const getStatusColor = (status) => {
  const statusMap = { 
    operational: 'green', active: 'green', maintenance_required: 'orange', 
    damaged: 'red', critical: 'red', decommissioned: 'gray', inactive: 'gray' 
  };
  return statusMap[status?.toLowerCase()] || 'gray';
};

const getHealthColor = (health) => {
  const healthMap = { good: 'green', warning: 'yellow', critical: 'red' };
  return healthMap[health?.toLowerCase()] || 'gray';
};

const formatCurrency = (value) => 
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);

const formatDate = (dateString) => 
  dateString ? new Date(dateString).toLocaleDateString('en-US', { 
    year: 'numeric', month: 'long', day: 'numeric' 
  }) : 'N/A';

const formatDateTime = (dateString) => 
  dateString ? new Date(dateString).toLocaleString('en-US', { 
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' 
  }) : 'N/A';

// ═════════════════════════════════════════════════════════════════════════════
// ENHANCED STAT CARD COMPONENT
// ═════════════════════════════════════════════════════════════════════════════
const EnhancedStatCard = ({ label, value, helpText, colorScheme = 'blue' }) => {
  const cardBg = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const labelColor = useColorModeValue('gray.600', 'gray.400');
  const valueColor = useColorModeValue(`${colorScheme}.600`, `${colorScheme}.300`);

  return (
    <MotionBox 
      variants={fadeIn} 
      whileHover={{ y: -2, boxShadow: 'lg' }}
      transition={{ duration: 0.2 }}
    >
      <Stat 
        px={4} 
        py={4} 
        bg={cardBg} 
        borderRadius="xl" 
        borderWidth="2px" 
        borderColor={borderColor} 
        boxShadow="md"
        _hover={{ borderColor: `${colorScheme}.300` }}
      >
        <StatLabel fontSize="xs" fontWeight="700" color={labelColor} textTransform="uppercase">
          {label}
        </StatLabel>
        <StatNumber fontSize="xl" color={valueColor} fontWeight="800">
          {value}
        </StatNumber>
        {helpText && (
          <StatHelpText fontSize="xs" color={labelColor}>
            {helpText}
          </StatHelpText>
        )}
      </Stat>
    </MotionBox>
  );
};

// --- UPDATE: per-asset simulation impact
const DAMAGE_SCHEME = { none: 'green', slight: 'yellow', moderate: 'orange', extensive: 'red', complete: 'red' };
const fmtH = (h) => (h == null ? '—' : `T${h >= 0 ? '+' : ''}${h}h`);

const SimulationImpactPanel = ({ record: r, title, cardBg, borderColor, textPrimary, textSecondary }) => {
  const probs = r.damage_probabilities || {};
  const stats = [
    r.damage_state && { label: 'Damage state', value: r.damage_state, scheme: DAMAGE_SCHEME[r.damage_state] },
    { label: 'Service impact', value: `${Math.round((r.functional_impact ?? r.impact_score ?? 0) * 100)}%`, scheme: r.state === 'critical' ? 'red' : r.state === 'warning' ? 'orange' : 'green' },
    r.peak_wind_kts != null && { label: 'Peak wind', value: `${r.peak_wind_kts} kt`, sub: r.peak_wind_at ? new Date(r.peak_wind_at).toLocaleString() : null },
    r.peak_flood_depth_m != null && { label: 'Peak flood depth', value: `${r.peak_flood_depth_m} m`, sub: r.flood_source && r.flood_source !== 'none' ? r.flood_source : null },
    r.time_to_flood_hours != null && { label: 'Flooding starts', value: fmtH(r.time_to_flood_hours), sub: `${r.hours_flooded}h flooded` },
    r.expected_repair_cost_usd != null && { label: 'Expected repair', value: formatCurrency(r.expected_repair_cost_usd) },
    r.restored_by_day != null && r.restoration_days > 0 && { label: 'Restored by', value: `day ${r.restored_by_day}`, sub: `${r.restoration_days}d repair` },
    r.simulated_psi != null && { label: 'Simulated pressure', value: `${r.simulated_psi} psi` },
    r.cascade_from_asset_id != null && { label: 'Impacted via', value: `asset #${r.cascade_from_asset_id}`, sub: r.edge_type || null },
  ].filter(Boolean);

  return (
    <VStack align="stretch" spacing={5}>
      <Alert status={r.state === 'critical' ? 'error' : r.state === 'warning' ? 'warning' : 'success'} borderRadius="xl" variant="left-accent">
        <AlertIcon />
        <Box>
          <AlertTitle fontWeight="700">{title || 'Simulation'}: {String(r.state || 'ok').toUpperCase()}</AlertTitle>
          <AlertDescription fontSize="sm" color={textSecondary}>
            {r.hardening_priority ? `Hardening priority: ${r.hardening_priority.replace(/_/g, ' ')}` : 'Simulated impact on this asset.'}
          </AlertDescription>
        </Box>
      </Alert>

      <Grid templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }} gap={3}>
        {stats.map(st => (
          <Box key={st.label} p={3} bg={cardBg} borderWidth="2px" borderColor={borderColor} borderRadius="xl">
            <Text fontSize="xs" fontWeight="700" color={textSecondary} textTransform="uppercase">{st.label}</Text>
            <Text fontSize="lg" fontWeight="800" color={st.scheme ? `${st.scheme}.500` : textPrimary} textTransform="capitalize">{st.value}</Text>
            {st.sub && <Text fontSize="xs" color={textSecondary}>{st.sub}</Text>}
          </Box>
        ))}
      </Grid>

      {probs.slight_or_worse != null && (
        <Box>
          <Text fontWeight="700" mb={2} color={textPrimary}>Damage probability</Text>
          <VStack align="stretch" spacing={2}>
            {[['Slight or worse', probs.slight_or_worse, 'yellow'], ['Moderate or worse', probs.moderate_or_worse, 'orange'], ['Extensive or worse', probs.extensive_or_worse, 'red'], ['Complete', probs.complete, 'red']].map(([label, v, scheme]) => (
              <Box key={label}>
                <Flex justify="space-between"><Text fontSize="xs" color={textSecondary}>{label}</Text><Text fontSize="xs" fontWeight="800">{Math.round((v ?? 0) * 100)}%</Text></Flex>
                <Progress value={(v ?? 0) * 100} size="sm" colorScheme={scheme} borderRadius="full" />
              </Box>
            ))}
          </VStack>
        </Box>
      )}

      {Array.isArray(r.recommended_actions) && r.recommended_actions.length > 0 && (
        <Box>
          <Text fontWeight="700" mb={2} color={textPrimary}>Recommended pre-storm actions</Text>
          <VStack align="stretch" spacing={2}>
            {r.recommended_actions.map(a => (
              <Flex key={a} gap={3} p={3} bg={cardBg} borderRadius="lg" borderWidth="1px" borderColor={borderColor}>
                <CheckCircleIcon color="orange.400" mt={1} boxSize={4} flexShrink={0} />
                <Text fontSize="sm" color={textSecondary}>{a}</Text>
              </Flex>
            ))}
          </VStack>
        </Box>
      )}
    </VStack>
  );
};
// --- END UPDATE

// ═════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═════════════════════════════════════════════════════════════════════════════
const AssetDetailModal = ({ 
  isOpen, 
  onClose, 
  asset,
  liveReadings = {},          // <-- Live WebSocket Telemetry 
  sensorPipelineLinks = {},   // <-- Topology Mapping
  onOpenTwin,                 // <-- Bridge to Digital Twin Drawer
  // --- UPDATE: preloaded page data + simulation context (all optional)
  issues: preloadedIssues = null,
  sensors: preloadedSensors = null,
  healthData: preloadedHealth = null,
  simulationRecord = null,
  simulationTitle = null,
  // --- END UPDATE
}) => {
  const toast = useToast();
  
  // State
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [healthData, setHealthData] = useState(null);
  const [issues, setIssues] = useState([]);
  const [sensorReadings, setSensorReadings] = useState([]);

  // ==================== ALL THEME HOOKS ====================
  const modalBg = useColorModeValue('white', 'gray.800');
  const headerGradient = useColorModeValue(
    'linear(to-r, blue.600, teal.500)', 
    'linear(to-r, blue.400, teal.400)'
  );
  const cardBg = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textPrimary = useColorModeValue('gray.800', 'white');
  const textSecondary = useColorModeValue('gray.600', 'gray.300');
  const textMuted = useColorModeValue('gray.500', 'gray.400');
  
  const sensorActiveBg = useColorModeValue('green.50', 'green.900');
  const sensorActiveBorder = useColorModeValue('green.200', 'green.700');
  
  const tabSelectedBg = useColorModeValue('blue.50', 'blue.900');
  const tabHoverBg = useColorModeValue('gray.100', 'gray.700');
  
  const tableHeaderBg = useColorModeValue('gray.50', 'gray.750');
  // --- UPDATE
  const requestIdRef = useRef(0);
  // --- END UPDATE

  // Dynamic Type Formatting
  const typeLabel = asset?.isSensor ? 'Sensor' : asset?.isPipeline ? 'Pipeline' : (asset?.type || asset?.asset_type || 'Asset');
  const assetName = asset?.name || `${typeLabel} ${asset?.id || asset?.device_id}`;
  const targetId = asset?.id || asset?.device_id;

  // ==================== DATA FETCHING ====================
  // --- UPDATE: preloaded data first, parallel fetches, stale guard
  const fetchAssetDetails = useCallback(async ({ forceHealth = false } = {}) => {
    if (!targetId || !asset) return;
    const requestId = ++requestIdRef.current;
    setLoading(true);

    try {
      const wantsHealth = !asset.isSensor && !asset.isPipeline;
      const [health, allIssuesResponse, allSensors] = await Promise.all([
        // Only fetch AI Health if it's a physical asset/well/pump (not a standalone sensor/pipeline)
        wantsHealth
          ? (preloadedHealth && !forceHealth ? Promise.resolve(preloadedHealth) : PredictiveFailureService.getAssetHealth(targetId).catch(() => null))
          : Promise.resolve(null),
        Array.isArray(preloadedIssues) ? Promise.resolve({ data: preloadedIssues }) : IssueService.fetchAllIssues().catch(() => ({ data: [] })),
        Array.isArray(preloadedSensors) ? Promise.resolve(preloadedSensors) : SensorService.fetchSensors().catch(() => []),
      ]);
      if (requestId !== requestIdRef.current) return; // a newer asset was opened

      const allIssues = allIssuesResponse?.data || [];
      const assetIssues = allIssues.filter(issue => 
        String(issue.asset_id) === String(targetId) || String(issue.assetId) === String(targetId) ||
        (issue.location && asset.latitude && asset.longitude && 
         Math.abs(issue.location.latitude - asset.latitude) < 0.001 &&
         Math.abs(issue.location.longitude - asset.longitude) < 0.001)
      );

      // --- CONTEXT-AWARE SENSOR MAPPING ---
      const assetSensors = (allSensors || []).filter(sensor => {
        const sId = sensor.device_id || sensor.id;
        
        // 1. Direct match for standalone sensor
        if (asset.isSensor && String(sId) === String(targetId)) return true;
        // 2. Direct database link
        if (String(sensor.linked_entity_id) === String(targetId)) return true;
        // 3. Topology mapping (pipeline -> sensor)
        if (String(sensorPipelineLinks[sId]) === String(targetId)) return true;
        // 4. Legacy fallbacks
        if (String(sensor.asset_id) === String(targetId) || String(sensor.assetId) === String(targetId)) return true;
        
        return false;
      });

      setHealthData(health);
      setIssues(assetIssues || []);
      setSensorReadings(assetSensors || []);
    } catch (error) {
      if (requestId !== requestIdRef.current) return;
      toast({ 
        title: 'Error Loading Details', 
        description: error.message || 'Failed to load asset details.', 
        status: 'error', 
        duration: 4000, 
        isClosable: true 
      });
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }, [targetId, asset, preloadedHealth, preloadedIssues, preloadedSensors, sensorPipelineLinks, toast]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchAssetDetails({ forceHealth: true });
    setRefreshing(false);
    toast({ 
      title: 'Data Refreshed', 
      description: 'Asset details have been updated.', 
      status: 'success', 
      duration: 2000, 
      isClosable: true,
      position: 'top-right',
    });
  };

  useEffect(() => {
    if (isOpen && asset) fetchAssetDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, targetId]);
  // --- END UPDATE

  // ==================== COMPUTED VALUES ====================
  const criticalIssues = useMemo(() => 
    issues.filter(i => i.severity === 'critical' || i.priority === 'high'), 
    [issues]
  );
  
  const failureRisk = useMemo(() => {
    if (!healthData?.failure_probability) return 0;
    return Math.round(healthData.failure_probability * 100);
  }, [healthData]);

  // ==================== HANDLERS ====================
  const handleExport = () => {
    const exportData = {
      asset: { 
        id: targetId, 
        name: assetName, 
        type: typeLabel, 
        status: asset?.status, 
        location: asset?.location || { lat: asset?.latitude, lng: asset?.longitude }
      },
      health: healthData,
      issues: issues.map(i => ({ 
        id: i.id, 
        title: i.title, 
        severity: i.severity, 
        status: i.status 
      })),
      sensors: sensorReadings.map(s => {
        const sId = s.device_id || s.id;
        const liveVal = liveReadings[sId];
        return {
          id: sId, 
          type: s.type, 
          value: liveVal ? (liveVal.psi ?? liveVal.level ?? liveVal.value ?? liveVal.flow_rate) : s.value, 
          status: liveVal ? 'active (live)' : s.status 
        };
      }),
      exportDate: new Date().toISOString(),
      // --- UPDATE
      simulation: simulationRecord ? { title: simulationTitle, impact: simulationRecord } : null,
      // --- END UPDATE
    };
    
    // --- UPDATE: Blob export
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', url);
    linkElement.setAttribute('download', `asset-${targetId}-details.json`);
    linkElement.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    // --- END UPDATE
    
    toast({ 
      title: 'Export Complete', 
      description: 'Asset details have been downloaded.', 
      status: 'success', 
      duration: 2000, 
      isClosable: true,
      position: 'top-right',
    });
  };

  // ==================== EARLY RETURN ====================
  if (!asset) return null;

  // ==================== RENDER ====================
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl" scrollBehavior="inside">
      <ModalOverlay backdropFilter="blur(10px)" bg="blackAlpha.400" />
      <MotionBox 
        initial={{ opacity: 0, scale: 0.95, y: 20 }} 
        animate={{ opacity: 1, scale: 1, y: 0 }} 
        exit={{ opacity: 0, scale: 0.95, y: 20 }} 
        transition={{ duration: 0.2 }}
      >
        <ModalContent 
          maxH="90vh" 
          bg={modalBg} 
          borderRadius="2xl" 
          boxShadow="2xl"
          mx={4}
        >
          {/* HEADER */}
          <ModalHeader borderBottomWidth="2px" borderColor={borderColor} pb={4}>
            <Flex justify="space-between" align="center">
              <Box flex={1}>
                <Text 
                  fontSize="2xl" 
                  fontWeight="800" 
                  bgGradient={headerGradient} 
                  bgClip="text"
                  noOfLines={1}
                >
                  {assetName}
                </Text>
                <HStack spacing={3} mt={1}>
                  <Text fontSize="sm" color={textMuted} fontWeight="600" fontFamily="monospace">
                    ID: {targetId}
                  </Text>
                  <Tag size="sm" colorScheme="blue">
                    {typeLabel.toUpperCase()}
                  </Tag>
                </HStack>
              </Box>
              <HStack spacing={2}>
                {onOpenTwin && (
                  <Button 
                    size="sm" 
                    colorScheme="purple" 
                    leftIcon={<Icon as={FaRobot} />} 
                    onClick={() => onOpenTwin(asset)} 
                    boxShadow="md" 
                    _hover={{ transform: 'translateY(-1px)', boxShadow: 'lg' }}
                  >
                    Launch Digital Twin
                  </Button>
                )}
                <Tooltip label="Refresh Data" hasArrow>
                  <IconButton 
                    icon={refreshing ? <Spinner size="sm" /> : <RepeatIcon />} 
                    size="sm" 
                    variant="ghost" 
                    onClick={handleRefresh} 
                    isDisabled={refreshing}
                    aria-label="Refresh"
                    _hover={{ bg: tabHoverBg }}
                  />
                </Tooltip>
                <Tooltip label="Export Details" hasArrow>
                  <IconButton 
                    icon={<DownloadIcon />} 
                    size="sm" 
                    variant="ghost" 
                    onClick={handleExport} 
                    aria-label="Export"
                    _hover={{ bg: tabHoverBg }}
                  />
                </Tooltip>
              </HStack>
            </Flex>
          </ModalHeader>
          
          <ModalCloseButton mt={2} />

          {/* BODY */}
          <ModalBody py={6}>
            {loading ? (
              <Flex justify="center" align="center" minH="400px">
                <VStack spacing={4}>
                  <Spinner size="xl" thickness="4px" color="blue.500" />
                  <Text color={textSecondary} fontWeight="600">
                    Loading asset details...
                  </Text>
                </VStack>
              </Flex>
            ) : (
              <Tabs colorScheme="blue" variant="enclosed" isLazy>
                <TabList borderBottomWidth="2px" borderColor={borderColor}>
                  <Tab 
                    fontWeight="700" 
                    fontSize="sm"
                    color={textSecondary}
                    _selected={{ color: textPrimary, borderColor: 'blue.500', bg: tabSelectedBg }}
                    _hover={{ bg: tabHoverBg }}
                  >
                    <HStack spacing={2}>
                      <InfoIcon boxSize={4} />
                      <Text>Overview</Text>
                    </HStack>
                  </Tab>
                  <Tab 
                    fontWeight="700" 
                    fontSize="sm"
                    color={textSecondary}
                    _selected={{ color: textPrimary, borderColor: 'blue.500', bg: tabSelectedBg }}
                    _hover={{ bg: tabHoverBg }}
                  >
                    <HStack spacing={2}>
                      <WarningIcon boxSize={4} />
                      <Text>Health</Text>
                      {healthData?.health_status === 'critical' && (
                        <Badge colorScheme="red" ml={1} fontSize="xs">!</Badge>
                      )}
                    </HStack>
                  </Tab>
                  <Tab 
                    fontWeight="700" 
                    fontSize="sm"
                    color={textSecondary}
                    _selected={{ color: textPrimary, borderColor: 'blue.500', bg: tabSelectedBg }}
                    _hover={{ bg: tabHoverBg }}
                  >
                    <HStack spacing={2}>
                      <SettingsIcon boxSize={4} />
                      <Text>Issues</Text>
                      {criticalIssues.length > 0 && (
                        <Badge colorScheme="red" fontSize="xs">{criticalIssues.length}</Badge>
                      )}
                    </HStack>
                  </Tab>
                  <Tab 
                    fontWeight="700" 
                    fontSize="sm"
                    color={textSecondary}
                    _selected={{ color: textPrimary, borderColor: 'blue.500', bg: tabSelectedBg }}
                    _hover={{ bg: tabHoverBg }}
                  >
                    <HStack spacing={2}>
                      <TimeIcon boxSize={4} />
                      <Text>Sensors</Text>
                      <Badge colorScheme="green" fontSize="xs">{sensorReadings.length}</Badge>
                    </HStack>
                  </Tab>
                  {/* --- UPDATE: simulation impact tab */}
                  {simulationRecord && (
                    <Tab 
                      fontWeight="700" 
                      fontSize="sm"
                      color={textSecondary}
                      _selected={{ color: textPrimary, borderColor: 'orange.500', bg: tabSelectedBg }}
                      _hover={{ bg: tabHoverBg }}
                    >
                      <HStack spacing={2}>
                        <Icon as={FaWind} boxSize={4} />
                        <Text>Simulation</Text>
                        <Badge colorScheme={simulationRecord.state === 'critical' ? 'red' : simulationRecord.state === 'warning' ? 'orange' : 'green'} fontSize="xs">
                          {Math.round((simulationRecord.impact_score ?? 0) * 100)}%
                        </Badge>
                      </HStack>
                    </Tab>
                  )}
                  {/* --- END UPDATE */}
                </TabList>

                <TabPanels>
                  {/* ═══ OVERVIEW TAB ═══ */}
                  <TabPanel px={0} py={6}>
                    <MotionBox variants={stagger} initial="hidden" animate="visible">
                      <VStack align="stretch" spacing={6}>
                        {/* Status Alert */}
                        {asset.status !== 'operational' && asset.status !== 'active' && (
                          <MotionBox variants={fadeIn}>
                            <Alert 
                              status={asset.status === 'damaged' || asset.status === 'critical' ? 'error' : 'warning'} 
                              borderRadius="xl"
                              variant="left-accent"
                            >
                              <AlertIcon />
                              <Box flex="1">
                                <AlertTitle fontWeight="700" fontSize="md">
                                  Asset Status: {asset.status}
                                </AlertTitle>
                                <AlertDescription fontSize="sm" color={textSecondary}>
                                  This asset requires attention. Check Health and Issues tabs for details.
                                </AlertDescription>
                              </Box>
                            </Alert>
                          </MotionBox>
                        )}

                        {/* Stats Grid */}
                        <MotionGrid 
                          templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }}
                          gap={4} 
                          variants={stagger}
                        >
                          <EnhancedStatCard 
                            label="Asset Type" 
                            value={typeLabel} 
                            colorScheme="blue" 
                          />
                          <EnhancedStatCard 
                            label="Status" 
                            value={asset.status || 'Unknown'} 
                            colorScheme={getStatusColor(asset.status)} 
                          />
                          {asset.material && (
                            <EnhancedStatCard 
                              label="Material" 
                              value={asset.material.toUpperCase()} 
                              colorScheme="teal" 
                            />
                          )}
                          {asset.diameter && (
                            <EnhancedStatCard 
                              label="Diameter" 
                              value={`${asset.diameter} in`} 
                              colorScheme="purple" 
                            />
                          )}
                          {asset.location && !asset.latitude && (
                            <EnhancedStatCard 
                              label="Location" 
                              value={asset.location} 
                              colorScheme="teal" 
                            />
                          )}
                          {asset.owner && (
                            <EnhancedStatCard 
                              label="Owner" 
                              value={asset.owner} 
                              colorScheme="purple" 
                            />
                          )}
                          {asset.installation_date && (
                            <EnhancedStatCard 
                              label="Installation Date" 
                              value={formatDate(asset.installation_date)} 
                              colorScheme="cyan" 
                            />
                          )}
                          {asset.last_maintenance_date && (
                            <EnhancedStatCard 
                              label="Last Maintenance" 
                              value={formatDate(asset.last_maintenance_date)} 
                              helpText={`${Math.floor((Date.now() - new Date(asset.last_maintenance_date)) / (1000 * 60 * 60 * 24))} days ago`}
                              colorScheme="orange" 
                            />
                          )}
                          {asset.estimated_value && (
                            <EnhancedStatCard 
                              label="Estimated Value" 
                              value={formatCurrency(asset.estimated_value)} 
                              colorScheme="green" 
                            />
                          )}
                          {asset.manufacturer && (
                            <EnhancedStatCard 
                              label="Manufacturer" 
                              value={asset.manufacturer} 
                              colorScheme="blue" 
                            />
                          )}
                        </MotionGrid>

                        <Divider borderColor={borderColor} />

                        {/* Description */}
                        {asset.description && (
                          <MotionBox variants={fadeIn}>
                            <Text fontWeight="700" mb={2} fontSize="md" color={textPrimary}>
                              Description
                            </Text>
                            <Text color={textSecondary} fontSize="sm" lineHeight="tall">
                              {asset.description}
                            </Text>
                          </MotionBox>
                        )}

                        {/* Coordinates */}
                        {(asset.latitude || asset.longitude) && (
                          <MotionBox variants={fadeIn}>
                            <Text fontWeight="700" mb={2} fontSize="md" color={textPrimary}>
                              Coordinates
                            </Text>
                            <HStack spacing={4}>
                              <Text fontSize="sm" color={textSecondary} fontFamily="monospace">
                                Lat: {asset.latitude ? parseFloat(asset.latitude).toFixed(6) : 'N/A'}
                              </Text>
                              <Text fontSize="sm" color={textSecondary} fontFamily="monospace">
                                Lng: {asset.longitude ? parseFloat(asset.longitude).toFixed(6) : 'N/A'}
                              </Text>
                              <Tooltip label="Open in Google Maps" hasArrow>
                                <IconButton 
                                  icon={<ExternalLinkIcon />} 
                                  size="xs" 
                                  variant="ghost" 
                                  onClick={() => window.open(
                                    `https://maps.google.com/?q=${asset.latitude},${asset.longitude}`, 
                                    '_blank'
                                  )} 
                                  aria-label="Open in Google Maps"
                                  colorScheme="blue"
                                />
                              </Tooltip>
                            </HStack>
                          </MotionBox>
                        )}
                      </VStack>
                    </MotionBox>
                  </TabPanel>

                  {/* ═══ HEALTH TAB ═══ */}
                  <TabPanel px={0} py={6}>
                    <VStack align="stretch" spacing={6}>
                      {healthData ? (
                        <>
                          {/* Health Status */}
                          <Flex justify="space-between" align="center">
                            <Text fontWeight="700" fontSize="md" color={textPrimary}>
                              Overall Health Status
                            </Text>
                            <Tag 
                              size="lg" 
                              colorScheme={getHealthColor(healthData.health_status)} 
                              borderRadius="full" 
                              fontWeight="800"
                              px={4}
                              py={2}
                            >
                              {healthData.health_status?.toUpperCase()}
                            </Tag>
                          </Flex>

                          {/* Failure Risk */}
                          <Box>
                            <Flex justify="space-between" align="center" mb={3}>
                              <Text fontWeight="700" fontSize="md" color={textPrimary}>
                                Failure Risk
                              </Text>
                              <Badge 
                                colorScheme={failureRisk > 70 ? 'red' : failureRisk > 40 ? 'orange' : 'green'} 
                                fontSize="md" 
                                px={3} 
                                py={1} 
                                borderRadius="full"
                                fontWeight="700"
                              >
                                {failureRisk}%
                              </Badge>
                            </Flex>
                            <Progress 
                              value={failureRisk} 
                              colorScheme={failureRisk > 70 ? 'red' : failureRisk > 40 ? 'orange' : 'green'} 
                              size="lg" 
                              hasStripe 
                              isAnimated={failureRisk > 70} 
                              borderRadius="full"
                            />
                            {failureRisk > 70 && (
                              <Text fontSize="sm" color="red.500" mt={2} fontWeight="600">
                                ⚠️ High failure risk detected. Immediate maintenance recommended.
                              </Text>
                            )}
                          </Box>

                          {/* Additional Health Metrics */}
                          <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={4}>
                            {healthData.predicted_failure_date && (
                              <GridItem colSpan={{ base: 1, md: 2 }}>
                                <Alert status="warning" borderRadius="xl" variant="left-accent">
                                  <AlertIcon />
                                  <Box flex="1">
                                    <AlertTitle fontWeight="700">
                                      Predicted Failure Date
                                    </AlertTitle>
                                    <AlertDescription fontSize="sm" color={textSecondary}>
                                      {formatDate(healthData.predicted_failure_date)} 
                                      ({Math.floor((new Date(healthData.predicted_failure_date) - Date.now()) / (1000 * 60 * 60 * 24))} days from now)
                                    </AlertDescription>
                                  </Box>
                                </Alert>
                              </GridItem>
                            )}

                            {healthData.remaining_useful_life && (
                              <GridItem>
                                <Box 
                                  p={4} 
                                  bg={cardBg} 
                                  borderRadius="xl" 
                                  borderWidth="2px" 
                                  borderColor={borderColor}
                                >
                                  <Stat>
                                    <StatLabel fontSize="xs" fontWeight="700" color={textMuted} textTransform="uppercase">
                                      Remaining Useful Life
                                    </StatLabel>
                                    <StatNumber fontSize="2xl" fontWeight="800" color={textPrimary}>
                                      {healthData.remaining_useful_life} {healthData.remaining_useful_life_unit || 'days'}
                                    </StatNumber>
                                  </Stat>
                                </Box>
                              </GridItem>
                            )}

                            {healthData.confidence_score && (
                              <GridItem>
                                <Box 
                                  p={4} 
                                  bg={cardBg} 
                                  borderRadius="xl" 
                                  borderWidth="2px" 
                                  borderColor={borderColor}
                                >
                                  <Stat>
                                    <StatLabel fontSize="xs" fontWeight="700" color={textMuted} textTransform="uppercase">
                                      Prediction Confidence
                                    </StatLabel>
                                    <StatNumber fontSize="2xl" fontWeight="800" color={textPrimary}>
                                      {Math.round(healthData.confidence_score * 100)}%
                                    </StatNumber>
                                    <StatHelpText fontSize="xs" color={textSecondary}>
                                      {healthData.confidence_score > 0.8 ? 'High' : healthData.confidence_score > 0.5 ? 'Medium' : 'Low'} confidence
                                    </StatHelpText>
                                  </Stat>
                                </Box>
                              </GridItem>
                            )}
                          </Grid>

                          {/* Recommended Actions */}
                          {healthData.recommended_actions && healthData.recommended_actions.length > 0 && (
                            <Box>
                              <Text fontWeight="700" mb={3} fontSize="md" color={textPrimary}>
                                Recommended Actions
                              </Text>
                              <VStack align="stretch" spacing={2}>
                                {healthData.recommended_actions.map((action, idx) => (
                                  <Flex 
                                    key={`action-${idx}`} 
                                    align="flex-start" 
                                    gap={3} 
                                    p={3} 
                                    bg={cardBg} 
                                    borderRadius="lg" 
                                    borderWidth="1px" 
                                    borderColor={borderColor}
                                  >
                                    <CheckCircleIcon color="blue.500" mt={1} boxSize={4} flexShrink={0} />
                                    <Text fontSize="sm" color={textSecondary}>
                                      {action}
                                    </Text>
                                  </Flex>
                                ))}
                              </VStack>
                            </Box>
                          )}
                        </>
                      ) : (
                        <Alert status="info" borderRadius="xl" variant="left-accent">
                          <AlertIcon />
                          <AlertTitle fontSize="md" color={textPrimary}>
                            No AI predictive health data available for this asset.
                          </AlertTitle>
                        </Alert>
                      )}
                    </VStack>
                  </TabPanel>

                  {/* ═══ ISSUES TAB ═══ */}
                  <TabPanel px={0} py={6}>
                    <VStack align="stretch" spacing={4}>
                      {issues.length > 0 ? (
                        <>
                          {criticalIssues.length > 0 && (
                            <Alert status="error" borderRadius="xl" variant="left-accent">
                              <AlertIcon />
                              <AlertTitle fontWeight="700">
                                {criticalIssues.length} critical issue{criticalIssues.length > 1 ? 's' : ''} require immediate attention
                              </AlertTitle>
                            </Alert>
                          )}

                          <Box 
                            borderWidth="1px" 
                            borderColor={borderColor} 
                            borderRadius="xl" 
                            overflow="hidden"
                          >
                            <Table variant="simple" size="sm">
                              <Thead bg={tableHeaderBg}>
                                <Tr>
                                  <Th fontWeight="800" color={textPrimary}>Issue</Th>
                                  <Th fontWeight="800" color={textPrimary}>Severity</Th>
                                  <Th fontWeight="800" color={textPrimary}>Status</Th>
                                  <Th fontWeight="800" color={textPrimary}>Date</Th>
                                </Tr>
                              </Thead>
                              <Tbody>
                                {issues.map((issue, idx) => (
                                  <Tr key={issue.id || `issue-${idx}`} _hover={{ bg: tabHoverBg }}>
                                    <Td>
                                      <Text fontWeight="600" fontSize="sm" color={textPrimary}>
                                        {issue.title}
                                      </Text>
                                      <Text fontSize="xs" color={textMuted} noOfLines={2}>
                                        {issue.description}
                                      </Text>
                                    </Td>
                                    <Td>
                                      <Badge 
                                        colorScheme={
                                          issue.severity === 'critical' ? 'red' : 
                                          issue.severity === 'high' ? 'orange' : 
                                          issue.severity === 'medium' ? 'yellow' : 'gray'
                                        }
                                        fontSize="xs"
                                        px={2}
                                        py={1}
                                        borderRadius="md"
                                      >
                                        {issue.severity}
                                      </Badge>
                                    </Td>
                                    <Td>
                                      <Badge 
                                        colorScheme={
                                          issue.status === 'resolved' ? 'green' : 
                                          issue.status === 'in_progress' ? 'blue' : 'gray'
                                        }
                                        fontSize="xs"
                                        px={2}
                                        py={1}
                                        borderRadius="md"
                                      >
                                        {issue.status}
                                      </Badge>
                                    </Td>
                                    <Td fontSize="xs" color={textSecondary}>
                                      {formatDate(issue.created_at)}
                                    </Td>
                                  </Tr>
                                ))}
                              </Tbody>
                            </Table>
                          </Box>
                        </>
                      ) : (
                        <Alert status="success" borderRadius="xl" variant="left-accent">
                          <AlertIcon />
                          <Box flex="1">
                            <AlertTitle fontWeight="700" color={textPrimary}>
                              No Issues Found
                            </AlertTitle>
                            <AlertDescription fontSize="sm" color={textSecondary}>
                              This asset has no recorded issues.
                            </AlertDescription>
                          </Box>
                        </Alert>
                      )}
                    </VStack>
                  </TabPanel>

                  {/* ═══ SENSORS TAB ═══ */}
                  <TabPanel px={0} py={6}>
                    <VStack align="stretch" spacing={4}>
                      {sensorReadings.length > 0 ? (
                        <>
                          <Flex justify="space-between" align="center">
                            <Text fontWeight="700" fontSize="md" color={textPrimary}>
                              {sensorReadings.length} linked sensors
                            </Text>
                          </Flex>

                          <Grid 
                            templateColumns={{ base: '1fr', md: 'repeat(auto-fill, minmax(250px, 1fr))' }} 
                            gap={4}
                          >
                            {sensorReadings.map((sensor, index) => {
                              const sId = sensor.device_id || sensor.id;
                              
                              // --- EXTRACT LIVE WEBSOCKET TELEMETRY ---
                              const liveData = liveReadings[sId];
                              const displayValue = liveData ? (liveData.psi ?? liveData.level ?? liveData.value ?? liveData.flow_rate) : sensor.value;
                              const isLive = !!liveData;
                              const lastSync = liveData?.timestamp || sensor.last_reading;

                              return (
                                <GridItem key={sId || `sensor-${index}`}>
                                  <Box 
                                    p={4} 
                                    borderWidth="2px" 
                                    borderRadius="xl" 
                                    bg={isLive || sensor.status === 'active' ? sensorActiveBg : cardBg} 
                                    borderColor={isLive || sensor.status === 'active' ? sensorActiveBorder : borderColor}
                                    boxShadow="sm"
                                    _hover={{ boxShadow: 'md' }}
                                    transition="all 0.2s"
                                  >
                                    <Flex justify="space-between" align="center" mb={3}>
                                      <Text fontWeight="700" fontSize="sm" color={textPrimary} fontFamily="mono">
                                        {sId}
                                      </Text>
                                      <Badge 
                                        colorScheme={isLive ? 'green' : sensor.status === 'active' ? 'blue' : 'gray'} 
                                        fontSize="xs" 
                                        px={2} 
                                        py={1} 
                                        borderRadius="full"
                                        fontWeight="700"
                                      >
                                        {isLive ? 'LIVE' : sensor.status}
                                      </Badge>
                                    </Flex>
                                    <Stat size="sm">
                                      <StatNumber fontSize="2xl" fontWeight="800" color={textPrimary} fontFamily="mono">
                                        {displayValue ?? 'N/A'}
                                      </StatNumber>
                                      <StatLabel color={textMuted} fontSize="xs" fontWeight="600" textTransform="uppercase">
                                        {sensor.type?.replace('_', ' ') || 'Reading'} {sensor.unit && `(${sensor.unit})`}
                                      </StatLabel>
                                      {lastSync && (
                                        <StatHelpText fontSize="xs" color={textSecondary} mt={1}>
                                          Sync: {formatDateTime(lastSync)}
                                        </StatHelpText>
                                      )}
                                    </Stat>
                                  </Box>
                                </GridItem>
                              );
                            })}
                          </Grid>
                        </>
                      ) : (
                        <Alert status="info" borderRadius="xl" variant="left-accent">
                          <AlertIcon />
                          <Box flex="1">
                            <AlertTitle fontWeight="700" color={textPrimary}>
                              No Sensors Found
                            </AlertTitle>
                            <AlertDescription fontSize="sm" color={textSecondary}>
                              This asset has no associated sensors or telemetry points.
                            </AlertDescription>
                          </Box>
                        </Alert>
                      )}
                    </VStack>
                  </TabPanel>

                  {/* --- UPDATE: SIMULATION TAB --- */}
                  {simulationRecord && (
                    <TabPanel px={0} py={6}>
                      <SimulationImpactPanel record={simulationRecord} title={simulationTitle} cardBg={cardBg} borderColor={borderColor} textPrimary={textPrimary} textSecondary={textSecondary} />
                    </TabPanel>
                  )}
                  {/* --- END UPDATE */}
                </TabPanels>
              </Tabs>
            )}
          </ModalBody>

          {/* FOOTER */}
          <ModalFooter borderTopWidth="2px" borderColor={borderColor}>
            <Button variant="ghost" mr={3} onClick={onClose} fontWeight="600">
              Close
            </Button>
            <Button 
              colorScheme="blue" 
              onClick={handleRefresh} 
              isLoading={refreshing} 
              fontWeight="700"
              boxShadow="sm"
              _hover={{ boxShadow: 'md' }}
            >
              Refresh
            </Button>
          </ModalFooter>
        </ModalContent>
      </MotionBox>
    </Modal>
  );
};

export default AssetDetailModal;